'use client';
import { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Icon,
  MobileHeader,
  MobileHeaderBackButton,
  MessageProvider,
} from '@ui/components';
import { CreatorProfile } from './creator-profile';
import { CreatorConversations } from 'app/(app)/creator/_components/creator-conversations';
import { useParams, usePathname, useRouter, useSearchParams } from 'next/navigation';
import { MobileHeaderUser } from '@components/mobile-header-user';
import { sendGAEvent } from '@next/third-parties/google';
import { useAppDispatch, useAppSelector } from '@lib/redux/store';
import { setCurrentTab, setScrollPosition } from '@lib/redux/badges/badges';
import { ImagePreviewProvider } from '@ui/hooks/file/ImagePreviewContext';
import { ROUTES } from '@constants/routes.constants';
import { useCreatorMessagesState } from 'app/(app)/creator/_components/creator-messages';
import {
  useGetNotificationByTypeQuery,
  useGetNotificationsQuery,
  useMarkAllAsReadMutation,
  useUpdateNotificationMutation,
} from '@lib/redux/api/notification-api';
import { NotificationPanel } from '@notification/component';
import { useChallengeState } from 'app/(app)/creator/challenges/challenge.state';
import { useNotebookState } from 'app/(app)/creator/contents/notebook.state';
import { useCreatorShellState } from './creator-shell.state';
import { setCreatorId, setShowMessages } from '@lib/redux/features/user';
import { useAuthSession } from '@lib/hooks/use-auth-session';
import WithAuthModal from '@components/with-auth-modal';
import { useInteractionGuard } from '@interaction-guard/index';
import { useCheckFanStatus } from '@lib/hooks/use-check-fan-status';
import { TABS } from './creator-shell.const';
import MobileTabFooter from './tabs/mobile-tab-footer';
import DesktopTab from './tabs/desktop-tab';
import { twMerge } from 'tailwind-merge';
import { DynamicRouteBuilder } from '@ui/constants';

interface CreatorShellProps {
  children: React.ReactNode;
  creatorSlug?: string;
  isDynamicRoute?: boolean;
}

const CreatorShell = ({ children, creatorSlug, isDynamicRoute }: CreatorShellProps) => {
  const { creatorId: creatorIdParam } = useParams();
  const { isAuthenticated } = useAuthSession();
  const currentTab = useAppSelector((state) => state.badges.currentTab || 'Badges');
  const searchParams = useSearchParams();
  const creatorId = searchParams.get('id') || searchParams.get('creatorId');
  const pathname = usePathname();
  const dispatch = useAppDispatch();
  const { user: userInfo } = useAppSelector((state) => state.user);
  const router = useRouter();
  const [storedCreatorId, setStoredCreatorId] = useState<string | null>(null);
  const { isIntercepted, message, pendingFn, clearInterception } = useInteractionGuard();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const creatorIdOptions =
    userInfo?.creator_id || storedCreatorId || creatorId || creatorIdParam?.toString();
  const { isFan: isFanOfSelectedCreator } = useCheckFanStatus(creatorIdOptions);

  const {
    isBadgesReady,
    isBadgesLoading,
    isAllBadgesLoading,
    creator,
    isCreatorLoading,
    badgesData,
    user,
    isUserLoading,
    isMessagesLoading,
    setMessagesLoading,
    onBack,
    isCommunityConversationSuccess,
    isCommunityConversationLoading,
    isPublicCommunityConversationSuccess,
    isPublicCommunityConversationLoading,
    communitySets,
    publicCommunitySets,
    newPostNotifications,
    newContentNotifications,
    isSubscribed,
  } = useCreatorShellState(creatorIdOptions, creatorSlug);

  const messageState = useCreatorMessagesState({ ordering: 'asc' });
  const { notebooks } = useNotebookState(creator.id);

  const { data: newNotificationsChallenge } = useGetNotificationByTypeQuery(
    { user_id: user?.id, type: 'challenge', creator_id: userInfo?.creator_id },
    { skip: !user?.id || !userInfo?.creator_id || !isAuthenticated },
  );

  const notificationData = useMemo(
    () => ({
      newPostNotifications,
      newContentNotifications,
      newNotificationsChallenge,
    }),
    [newPostNotifications, newContentNotifications, newNotificationsChallenge],
  );

  const creatorHasCommunity = useMemo(() => {
    if (!isAuthenticated || !isFanOfSelectedCreator) {
      return (
        isPublicCommunityConversationSuccess &&
        !isPublicCommunityConversationLoading &&
        publicCommunitySets.length > 0 &&
        publicCommunitySets[0]?.last_message !== null
      );
    }

    return (
      isCommunityConversationSuccess &&
      !isCommunityConversationLoading &&
      communitySets.length > 0 &&
      communitySets[0]?.last_message !== null
    );
  }, [
    isCommunityConversationSuccess,
    isCommunityConversationLoading,
    communitySets.length,
    isPublicCommunityConversationSuccess,
    isPublicCommunityConversationLoading,
    publicCommunitySets.length,
    isFanOfSelectedCreator,
  ]);
  const { challenges } = useChallengeState(creator.id);
  const creatorChallenge = useMemo(
    () => challenges.find((c) => c.status === 'active' || c.status === 'archived'),
    [challenges],
  );

  const creatorHasContents = useMemo(() => {
    return notebooks.length > 0;
  }, [notebooks]);

  const filteredTabs = useMemo(() => {
    return TABS.filter((tab) => {
      if (tab.id === 'Content' && !creatorHasContents) return false;
      if (tab.id === 'Challenges' && !creatorChallenge) return false;
      if (tab.id === 'Community' && !creatorHasCommunity) return false;
      return true;
    });
  }, [creatorHasContents, creatorChallenge, creatorHasCommunity]);

  const isLoading = useMemo(() => {
    return (
      isCreatorLoading ||
      isBadgesLoading ||
      isAllBadgesLoading ||
      isCommunityConversationLoading ||
      isMessagesLoading ||
      isUserLoading ||
      isPublicCommunityConversationLoading
    );
  }, [
    isCreatorLoading,
    isBadgesLoading,
    isAllBadgesLoading,
    isCommunityConversationLoading,
    isMessagesLoading,
    isUserLoading,
    isPublicCommunityConversationLoading,
  ]);

  useEffect(() => {
    if (typeof window !== 'undefined' && userInfo?.creator_id !== '') {
      window.sessionStorage.setItem('currentCreatorId', userInfo?.creator_id);
      const stored = window.sessionStorage.getItem('currentCreatorId');
      setStoredCreatorId(stored);
    }
    if (creatorId) {
      dispatch(setCreatorId(creatorId));
    }
  }, [creatorId]);

  // Only handle tab param case
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      const foundTab = TABS.find((tab) => tab.id === tabParam);
      if (foundTab) {
        dispatch(setCurrentTab(foundTab.id));
      }
    }
  }, [searchParams]);

  const handleTabClick = (tabId: string) => {
    const tab = TABS.find((t) => t.id === tabId);
    dispatch(setCurrentTab(tab.id));
    dispatch(setScrollPosition(''));

    switch (tab.id) {
      case 'Badges':
        handleBadgesTabClick();
        break;
      case 'Community':
        handleCommunityTabClick();
        break;
      case 'Leaderboards':
        handleLeaderboardsTabClick();
        break;
      case 'Content':
        handleContentTabClick();
        break;
      case 'Challenges':
        handleChallengesTabClick();
        break;
      case 'About':
        handleAboutTabClick();
        break;
      default:
    }

    sendGAEvent('event', `${tab.id}TabClicked`, { creatorId });
  };

  const handleBadgesTabClick = () => {
    if (isDynamicRoute) {
      const badgesRoute = DynamicRouteBuilder.badges(creatorSlug);
      router.push(badgesRoute);
    } else {
      router.push(`${ROUTES.creator}?id=${userInfo?.creator_id}&tab=Badges`);
    }
  };

  const handleCommunityTabClick = () => {
    const baseCommunitySet =
      isAuthenticated && isFanOfSelectedCreator ? communitySets : publicCommunitySets;
    const communityId = baseCommunitySet[0]?.conversation?.id;

    if (!communityId) return;

    if (isDynamicRoute) {
      const communityRoute = DynamicRouteBuilder.communityBySlug(creatorSlug, communityId);
      router.push(communityRoute);
    } else {
      router.push(`${ROUTES.community}/?slug=${communityId}`);
    }
  };

  const handleLeaderboardsTabClick = () => {
    if (isDynamicRoute) {
      const leaderboardsRoute = DynamicRouteBuilder.leaderboards(creatorSlug);
      router.push(leaderboardsRoute);
    } else {
      router.push(`${ROUTES.creator}?id=${userInfo?.creator_id}&tab=Leaderboards`);
    }
  };

  const handleContentTabClick = () => {
    if (isDynamicRoute) {
      const contentRoute = DynamicRouteBuilder.contents(creatorSlug);
      router.push(contentRoute);
    } else {
      router.push(`${ROUTES.contents}?id=${userInfo?.creator_id}&tab=Content`);
    }
  };

  const handleChallengesTabClick = () => {
    if (isDynamicRoute) {
      const challengesRoute = DynamicRouteBuilder.challenges(creatorSlug);
      router.push(challengesRoute);
    } else {
      router.push(`${ROUTES.challenges}?id=${userInfo?.creator_id}&tab=Challenges`);
    }
  };

  const handleAboutTabClick = () => {
    if (isDynamicRoute) {
      const aboutRoute = DynamicRouteBuilder.about(creatorSlug);
      router.push(aboutRoute);
    } else {
      router.push(`${ROUTES.creator}?id=${userInfo?.creator_id}&tab=About`);
    }
  };

  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    if (!initialLoad) {
      sendGAEvent('event', `${currentTab}TabClicked`, { creatorId });
    } else if (currentTab !== 'Badges') {
      sendGAEvent('event', `${currentTab}TabClicked`, { creatorId });
      setInitialLoad(false);
    } else {
      setInitialLoad(false);
    }
  }, [currentTab]);

  const openMessagePanel = () => {
    sendGAEvent('event', 'MessageButtonClicked', { creatorId });
    dispatch(setShowMessages(true));
  };

  function onOpenAbout() {
    dispatch(setCurrentTab('About'));
    router.push(`${ROUTES.creator}?id=${userInfo?.creator_id}&tab=About`);
  }

  const handleSettingsRoute = () =>
    router.push(`${ROUTES.settingsMobile}/?tab=notification-preference`);

  const controlDisplay = useMemo(() => {
    // Paths that should hide the creator profile
    const hideProfilePaths = [
      /\/fan-profile/,
      /\/fans/,
      /\/community-guidelines/,
      /\/creator\/community\/create-post/,
      /\/creator\/community\/post/,
      /\/creators\/[^/]+\/communities\/[^/]+\/posts/,
      /\/creator\/contents\/content/,
    ];

    const shouldHideProfile = hideProfilePaths.some((pattern) => pattern.test(pathname));

    // Show profile if path is not in hide list and has 4 or fewer segments
    const creatorProfile = !shouldHideProfile && pathname?.split('/')?.length <= 4;

    const isNotHome =
      pathname !== '/' &&
      pathname !== '/settings/' &&
      pathname !== '/faq/' &&
      pathname !== '/settings/mobile/';

    return { creatorProfile, isNotHome };
  }, [pathname]);

  useEffect(() => {
    if (isIntercepted) {
      if (isAuthenticated && isFanOfSelectedCreator && pendingFn) {
        // Execute the function if authenticated
        pendingFn.fn(...pendingFn.args);
        clearInterception();
      } else if (!isAuthenticated || !isFanOfSelectedCreator) {
        setShowAuthModal(true);
      }
    }
  }, [isIntercepted, isAuthenticated, isFanOfSelectedCreator]);

  return (
    <div
      className={`lg:bg-white bg-bgGrey lg:pb-0 ${controlDisplay.isNotHome ? 'pb-0' : ''} ${
        !isAuthenticated ? 'lg:max-w-[80vw] mx-auto' : ''
      }`}>
      {isAuthenticated && isFanOfSelectedCreator && (
        <MessageProvider userType="fan" state={messageState}>
          <ImagePreviewProvider>
            <CreatorConversations
              onClose={() => dispatch(setShowMessages(false))}
              creator={creator}
              setMessagesLoading={setMessagesLoading}
              hasArchivedMessages={creator.hasArchivedMessages}
            />
          </ImagePreviewProvider>
        </MessageProvider>
      )}

      {isAuthenticated && controlDisplay.isNotHome && (
        <MobileHeader>
          <MobileHeaderBackButton onBack={onBack} />
          <div className="flex items-center gap-4">
            <NotificationPanel
              useMarkAllAsReadMutation={useMarkAllAsReadMutation}
              useUpdateNotificationMutation={useUpdateNotificationMutation}
              useGetNotificationsQuery={useGetNotificationsQuery}
              onSettings={handleSettingsRoute}
              user_id={user?.id}>
              {(unread_notifications) => (
                <Button type="icon" className="relative">
                  <Icon type="notification" />
                  {Boolean(unread_notifications) && (
                    <span className="absolute top-1 right-0 bg-primary rounded-full px-1 text-xs text-white">
                      {unread_notifications}
                    </span>
                  )}
                </Button>
              )}
            </NotificationPanel>

            <MobileHeaderUser
              isLoading={isUserLoading}
              avatar={user ? user.avatar : ''}
              username={user ? user.username : ''}
              isSubscribed={isSubscribed}
            />
          </div>
        </MobileHeader>
      )}

      {controlDisplay.isNotHome && (
        <>
          {controlDisplay.creatorProfile && (
            <div className="lg:block mb-3 lg:mb-0 mt-2">
              <CreatorProfile
                name={creator.name}
                avatar={creator.avatar}
                banner={creator.banner}
                notifications={creator?.notifications}
                badgeAmount={isBadgesReady ? badgesData.badgeAmount : 0}
                isLoading={isCreatorLoading}
                creatorId={creator.id}
                score={creator?.score}
                onBio={onOpenAbout}
                isAboutActive={currentTab === 'About'}
                openMessagePanel={openMessagePanel}
                isMessagesLoading={isCreatorLoading}
              />
            </div>
          )}

          <DesktopTab
            filteredTabs={filteredTabs}
            isLoading={isLoading}
            notificationData={notificationData}
            currentTab={currentTab}
            isAuthenticated={isAuthenticated}
            creatorProfile={controlDisplay.creatorProfile}
            handleTabClick={handleTabClick}
          />
        </>
      )}

      {controlDisplay.isNotHome && (
        <MobileTabFooter
          filteredTabs={filteredTabs}
          isLoading={isLoading}
          notificationData={notificationData}
          currentTab={currentTab}
          creator={creator}
          isAuthenticated={isAuthenticated}
          isFanOfSelectedCreator={isFanOfSelectedCreator}
          handleTabClick={handleTabClick}
        />
      )}

      <WithAuthModal
        creatorInfo={{ ...userInfo.creatorInfo, creator_id: userInfo?.creator_id }}
        message={message || null}
        open={showAuthModal}
        onOpenChange={(open) => {
          setShowAuthModal(open);
          if (!open) clearInterception();
        }}
      />
      <div
        className={twMerge(
          `${isAuthenticated && controlDisplay.isNotHome ? 'px-2 lg:px-14 ' : 'px-0'}`,
        )}>
        {children}
      </div>
    </div>
  );
};

export default CreatorShell;
